// Upload engine: create upload → PUT pieces (bounded concurrency, idempotent retries) → POST commit with the
// whole-file SHA-256 → "uploaded" only when the server's committed hash equals the local one.
import { createApi } from './api.js';
import { classify, isTransient, MESSAGES, TransferFailure } from './errors.js';
import { MAX_PIECE_LENGTH, extOf } from './manifest.js';
import { buildSnapshot } from './snapshot.js';
import { createInlineHasher } from './file-hash.js';

const isAbort = (e) => e && (e.name === 'AbortError' || e.code === 20);
const newId = (p) => `${p}-${(globalThis.crypto && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2))}`;
const defaultSleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * @param {object} o
 * @param {Blob & {name?:string}} o.file
 * @param {string} [o.localId] id the view uses for this file (`file.id` in upload snapshots)
 * @param {string} o.brand
 * @param {string} [o.password]
 * @param {boolean} [o.isPublic=false] public is explicit and permission-gated server-side
 * @param {string|(() => string|null)} [o.operatorToken] runtime operator credential (isolated demo, Addendum B); sent only on POST /uploads
 * @param {() => void} [o.onOperatorRejected] called when the server refuses the credential (host forgets it)
 */
export function createUpload(o) {
  const api = o.api ?? createApi();
  const sleep = o.sleep ?? defaultSleep;
  const concurrency = Math.max(1, Math.min(8, o.concurrency ?? 3));
  const maxTransient = o.maxTransientFailures ?? 6;
  const hasherFactory = o.fileHasher ?? createInlineHasher;
  const transferId = o.transferId ?? newId('up');
  const file = o.file;
  const name = o.name ?? file.name ?? 'file';
  const size = file.size;
  const listeners = new Set(o.onSnapshot ? [o.onSnapshot] : []);

  let status = 'idle', error = null, sequence = 0, gen = 0;
  let upload = null; // { uploadId, token, pieceLength }
  let acked = null; // Uint8Array per piece
  let uploadedBytes = 0, transient = 0;
  let shaPromise = null;
  let result = null;
  const inflight = new Map();
  const meter = { at: 0, bytes: 0, rate: null };

  function snapshot() {
    sequence++;
    const t = Date.now();
    if (meter.at && t - meter.at >= 250) { const inst = (uploadedBytes - meter.bytes) / ((t - meter.at) / 1000); meter.rate = meter.rate == null ? inst : meter.rate * 0.7 + inst * 0.3; meter.at = t; meter.bytes = uploadedBytes; }
    else if (!meter.at) { meter.at = t; meter.bytes = uploadedBytes; }
    return buildSnapshot({
      brand: o.brand, transferId, sequence, direction: 'upload', mode: 'https', status,
      file: { id: o.localId ?? transferId, name, type: extOf(name) || null, sizeBytes: size },
      states: null, uploadedBytes,
      metrics: { uploadBytesPerSecond: status === 'uploading' ? meter.rate : null },
      storage: null, error,
    });
  }
  function emit() { const s = snapshot(); for (const fn of listeners) { try { fn(s); } catch (e) { console.error(e); } } }
  function setStatus(s, err = null) { status = s; error = err; emit(); }
  function fail(code, msg) { gen++; for (const [, c] of inflight) c.abort(); setStatus('error', { code, message: msg || MESSAGES[code] || MESSAGES['upload-failed'] }); }

  async function wholeSha() {
    const h = hasherFactory();
    try {
      const step = 4 * 1024 * 1024;
      for (let off = 0; off < size; off += step) await h.update(new Uint8Array(await file.slice(off, Math.min(size, off + step)).arrayBuffer()));
      return await h.digest();
    } finally { h.close(); }
  }

  async function sendPiece(i, myGen) {
    const pl = upload.pieceLength, start = i * pl, end = Math.min(size, start + pl);
    const ctrl = new AbortController();
    inflight.set(i, ctrl);
    try {
      const bytes = new Uint8Array(await file.slice(start, end).arrayBuffer());
      await api.putPiece(upload, i, bytes, ctrl.signal);
      if (myGen !== gen) return;
      if (!acked[i]) { acked[i] = 1; uploadedBytes += end - start; }
      transient = 0;
    } finally { inflight.delete(i); }
  }

  async function run(myGen) {
    const T = acked.length;
    let next = 0;
    const worker = async () => {
      while (myGen === gen) {
        while (next < T && acked[next]) next++;
        if (next >= T) return;
        const i = next++;
        for (;;) {
          try { await sendPiece(i, myGen); emit(); break; }
          catch (e) {
            if (myGen !== gen || isAbort(e)) return;
            const k = classify(e);
            if (k === 'too-large') throw new TransferFailure('too-large', MESSAGES['too-large']);
            if (!isTransient(k) || ++transient > maxTransient) throw new TransferFailure('upload-failed', MESSAGES['upload-failed']);
            await sleep(e.retryAfterMs ?? Math.min(10_000, 400 * 2 ** (transient - 1)));
            if (myGen !== gen) return;
          }
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, T || 1) }, worker));
  }

  async function go(from) {
    const myGen = ++gen;
    setStatus(from);
    try {
      if (!upload) {
        const operatorToken = typeof o.operatorToken === 'function' ? o.operatorToken() : o.operatorToken;
        if (!operatorToken) throw new TransferFailure('operator-required', MESSAGES['operator-required']);
        const r = await api.createUpload({ name, sizeBytes: size, mime: file.type || 'application/octet-stream', password: o.password, isPublic: typeof o.isPublic === 'function' ? !!o.isPublic() : !!o.isPublic, operatorToken });
        if (myGen !== gen) return;
        if (!r || typeof r.uploadId !== 'string' || typeof r.token !== 'string' || !Number.isSafeInteger(r.pieceLength) || r.pieceLength < 1 || r.pieceLength > MAX_PIECE_LENGTH) throw new TransferFailure('upload-failed', MESSAGES['upload-failed']);
        upload = { uploadId: r.uploadId, token: r.token, pieceLength: r.pieceLength };
        acked = new Uint8Array(size === 0 ? 0 : Math.ceil(size / r.pieceLength));
      }
      if (!shaPromise) shaPromise = wholeSha();
      setStatus('uploading');
      await run(myGen);
      if (myGen !== gen) return;
      setStatus('committing');
      const sha = await shaPromise;
      if (myGen !== gen) return;
      const c = await api.commitUpload(upload, sha);
      if (myGen !== gen) return;
      if (c && typeof c.sha256 === 'string' && c.sha256.toLowerCase() !== sha) return fail('commit-mismatch');
      result = { ...c, sha256: sha };
      setStatus('ready');
    } catch (e) {
      if (myGen !== gen || isAbort(e)) return;
      if (e instanceof TransferFailure) return fail(e.code, e.message);
      const k = classify(e);
      if (k === 'too-large') return fail('too-large');
      if (!upload && (k === 'unauthorized' || k === 'denied')) { o.onOperatorRejected?.(); return fail('operator-rejected'); }
      fail('upload-failed');
    }
  }

  return {
    transferId,
    get status() { return status; },
    get result() { return result; },
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    snapshot,
    start: () => (status === 'idle' ? go('uploading') : undefined),
    async pause() { if (status !== 'uploading') return; gen++; for (const [, c] of inflight) c.abort(); setStatus('paused'); },
    resume: () => (status === 'paused' ? go('resuming') : undefined),
    retry: () => (status === 'error' ? go('resuming') : undefined),
    async cancel() { if (['ready', 'cancelled', 'idle'].includes(status)) return; gen++; for (const [, c] of inflight) c.abort(); setStatus('cancelled'); },
    dispatch(a) {
      switch (a && a.type) {
        case 'pause': return this.pause(), true;
        case 'resume': return this.resume(), true;
        case 'retry': return this.retry(), true;
        case 'cancel': return this.cancel(), true;
        default: return false;
      }
    },
    destroy() { gen++; for (const [, c] of inflight) c.abort(); listeners.clear(); },
  };
}
